import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Star, ShieldCheck, Truck, RefreshCcw, Wrench, ArrowLeft } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    api.get(`/products/${id}`).then(({ data }) => setProduct(data)).catch(() => navigate("/shop"));
  }, [id, navigate]);

  if (!product) return <div className="max-w-7xl mx-auto px-5 py-20 text-neutral-400">Loading…</div>;

  const discount = Math.max(0, Math.round(((product.mrp - product.price) / product.mrp) * 100));

  const handleAdd = async () => {
    if (!user) { toast.error("Please sign in"); navigate("/login"); return; }
    try { await add(product.product_id, qty); toast.success("Added to cart"); }
    catch { toast.error("Could not add"); }
  };

  const handleBuy = async () => {
    await handleAdd();
    navigate("/cart");
  };

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8" data-testid="product-detail-page">
      <button
        onClick={() => navigate(-1)}
        className="text-sm text-neutral-400 hover:text-white flex items-center gap-1 mb-6"
        data-testid="back-button"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-6">
          <div className="vm-card overflow-hidden aspect-square bg-ink-800 relative">
            <img src={product.image} alt={product.name} className="absolute inset-0 w-full h-full object-cover" />
            {discount > 0 && (
              <span className="absolute top-4 left-4 vm-chip vm-chip-accent">-{discount}%</span>
            )}
          </div>
        </div>
        <div className="lg:col-span-6">
          <p className="vm-overline">{product.brand} • {product.category}</p>
          <h1 className="font-display text-3xl md:text-5xl font-bold mt-3 leading-tight tracking-tight">
            {product.name}
          </h1>
          <div className="flex items-center gap-3 mt-3">
            <span className="flex items-center gap-1 text-sm text-neutral-300">
              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" /> {product.rating} rating
            </span>
            <span className="text-sm text-neutral-500">•</span>
            <span className="text-sm text-neutral-300">{product.stock} in stock</span>
          </div>

          <div className="flex items-end gap-3 mt-6">
            <p className="font-display text-4xl font-bold">{inr(product.price)}</p>
            {product.mrp > product.price && (
              <p className="text-base text-neutral-500 line-through mb-1">{inr(product.mrp)}</p>
            )}
            {discount > 0 && <p className="text-base text-blaze-500 font-bold mb-1">-{discount}%</p>}
          </div>
          <p className="text-xs text-neutral-500 mt-1">Inclusive of all taxes</p>

          <p className="text-neutral-300 mt-6 leading-relaxed">{product.description}</p>

          {product.specs && Object.keys(product.specs).length > 0 && (
            <div className="mt-6 vm-card p-5">
              <p className="vm-overline mb-3">Tech specs</p>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(product.specs).map(([k, v]) => (
                  <div key={k} className="text-sm">
                    <p className="text-neutral-500 capitalize">{k}</p>
                    <p className="text-white font-medium font-mono text-xs mt-1">{v}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 mt-8">
            <div className="flex items-center gap-1 vm-card px-1 py-1">
              <button
                className="h-9 w-9 rounded-full hover:bg-white/5 text-lg"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                data-testid="qty-decrease"
              >−</button>
              <span className="w-8 text-center font-mono" data-testid="qty-value">{qty}</span>
              <button
                className="h-9 w-9 rounded-full hover:bg-white/5 text-lg"
                onClick={() => setQty((q) => Math.min(product.stock || 99, q + 1))}
                data-testid="qty-increase"
              >+</button>
            </div>
            <button className="vm-btn-ghost flex-1" onClick={handleAdd} data-testid="add-to-cart-btn">Add to cart</button>
            <button className="vm-btn-primary flex-1" onClick={handleBuy} data-testid="buy-now-btn">Buy now</button>
          </div>

          <div className="grid grid-cols-3 gap-3 mt-8">
            <div className="vm-card p-4 text-center">
              <Truck className="h-5 w-5 text-volt-400 mx-auto" />
              <p className="text-xs text-neutral-400 mt-2">30-min delivery</p>
            </div>
            <div className="vm-card p-4 text-center">
              <ShieldCheck className="h-5 w-5 text-volt-400 mx-auto" />
              <p className="text-xs text-neutral-400 mt-2">Brand warranty</p>
            </div>
            <div className="vm-card p-4 text-center">
              <RefreshCcw className="h-5 w-5 text-volt-400 mx-auto" />
              <p className="text-xs text-neutral-400 mt-2">7-day return</p>
            </div>
          </div>

          <div className="mt-8 vm-card p-5 flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold">Need installation or repair?</p>
              <p className="text-xs text-neutral-400 mt-1">Book a technician at your slot.</p>
            </div>
            <Link to="/services" className="vm-btn-ghost text-sm flex items-center gap-2" data-testid="book-service-cta">
              <Wrench className="h-4 w-4" /> Book
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
